/**
 * Expedition (docs/spec/07 §2): an endless run of generated caves, each deeper. Best depth and
 * score, PLAY on a fresh seed or on today's seed (the same run for everyone today).
 */

import { BONUS_EVERY, DEEP_FROM } from '../../caves/generator';
import { atlasFor } from '../../render/atlas';
import { BLUE, CYAN, DARK_GREY, GREY, LIGHT_BLUE } from '../../render/palette';
import type { Screen } from '../../render/screen';
import { seedFromText } from '../../sim/rng';
import type { MenuAction, PointerInput } from '../scene';
import { TileField } from '../kit/backdrop';
import { UiScene } from '../kit/base';
import { drawButton, drawPanel } from '../kit/chrome';
import type { Rect } from '../kit/menuModel';
import { inRect, wrapIndex } from '../kit/menuModel';
import { commas, isoDate } from '../kit/text';
import { ACCENT, animFrame, CAVE_A, FAINT, HEADING, INK, MUTED } from '../kit/theme';

const SHAFT: Rect = { x: 14, y: 52, w: 72, h: 118 };
const BUTTONS: readonly { id: 'random' | 'today'; label: string; rect: Rect }[] = [
  { id: 'random', label: 'PLAY', rect: { x: 106, y: 128, w: 204, h: 15 } },
  { id: 'today', label: "PLAY TODAY'S SEED", rect: { x: 106, y: 148, w: 204, h: 15 } },
];
/** How fast the shaft sinks, pixels per second. */
const SHAFT_SPEED = 30;

export class ExpeditionScene extends UiScene {
  protected readonly heading = 'EXPEDITION';
  private readonly shaft = new TileField([DARK_GREY, GREY, LIGHT_BLUE], SHAFT_SPEED, 0);
  private sel = 0;

  protected override describe(): string {
    const r = this.services.records.expedition();
    return `Expedition. Best depth ${r.depth}, best score ${r.score}. ${BUTTONS[this.sel]?.label ?? ''}`;
  }

  protected override hint(): string {
    return `▲▼ CHOOSE   ${this.words().ok} DIG   ${this.words().back} BACK`;
  }

  protected paint(screen: Screen): void {
    screen.text('AN ENDLESS RUN OF NEW CAVES, EACH ONE', 160, 24, INK, 'center');
    screen.text('DEEPER AND HARDER. HOW FAR CAN YOU GO?', 160, 34, INK, 'center');
    this.paintShaft(screen);
    const r = this.services.records.expedition();
    screen.text('BEST DEPTH', 106, 56, HEADING);
    const depth = r.depth > 0 ? String(r.depth) : '-';
    screen.text(depth, 107, 69, 0, 'left', 2);
    screen.text(depth, 106, 68, r.depth > 0 ? ACCENT : FAINT, 'left', 2);
    screen.text('BEST SCORE', 206, 56, HEADING);
    const score = r.score > 0 ? commas(r.score) : '-';
    screen.text(score, 207, 69, 0, 'left', 2);
    screen.text(score, 206, 68, r.score > 0 ? INK : FAINT, 'left', 2);
    screen.text(`DEEP ELEMENTS FROM DEPTH ${DEEP_FROM}`, 106, 96, MUTED);
    screen.text(`BONUS CAVE EVERY ${BONUS_EVERY} DEPTHS`, 106, 106, MUTED);
    BUTTONS.forEach((b, i) => drawButton(screen, b.rect, b.label, i === this.sel ? 'selected' : 'idle', this.ms));
    const { year, month, day } = this.services.today();
    screen.text(isoDate(year, month, day), 208, 166, FAINT, 'center');
  }

  /** A shaft sinking through rock, Digby riding down it, the depth ticking by. */
  private paintShaft(screen: Screen): void {
    const s = SHAFT;
    drawPanel(screen, s, BLUE, CYAN);
    this.shaft.draw(screen, this.ms, s.y + 1, s.y + s.h - 1, s.x + 1, s.x + s.w - 1, false);
    const cx = s.x + s.w / 2 - 8;
    screen.fill(cx - 2, s.y + 1, 20, s.h - 2, 0);
    const bob = Math.round(Math.sin(this.ms / 300) * 2);
    const anim = animFrame(this.ms);
    atlasFor(CAVE_A).draw(screen.ctx, anim % 60 < 4 ? 'digbyBlink' : 'digbyStand', 0, cx, s.y + s.h / 2 - 8 + bob);
    for (let i = 0; i < 8; i++) {
      const y = s.y + 4 + ((i * 16 - ((this.ms * SHAFT_SPEED) / 1000)) % s.h + s.h) % s.h;
      screen.fill(s.x + s.w - 6, Math.round(y), 4, 1, FAINT);
    }
    screen.fill(s.x + 1, s.y + s.h - 12, s.w - 2, 11, 0);
    const drop = Math.floor(this.ms / 180) % 3;
    for (let i = 0; i < 3; i++) {
      screen.text('▼', s.x + s.w / 2 - 16 + i * 12, s.y + s.h - 10, i === drop ? ACCENT : FAINT);
    }
  }

  private play(id: 'random' | 'today'): void {
    this.sound('select');
    let seed: number;
    if (id === 'today') {
      const { year, month, day } = this.services.today();
      seed = seedFromText(`EXPEDITION ${isoDate(year, month, day)}`);
    } else seed = 1 + Math.floor(Math.random() * 0x7ffffffe);
    this.host.go({ to: 'play', request: { mode: 'expedition', seed } });
  }

  protected onAction(a: MenuAction): void {
    if (a === 'up' || a === 'down' || a === 'left' || a === 'right') {
      this.sel = wrapIndex(this.sel + (a === 'up' || a === 'left' ? -1 : 1), BUTTONS.length);
      this.sound('move');
      this.say(BUTTONS[this.sel]?.label ?? '');
    } else if (a === 'confirm') this.play(BUTTONS[this.sel]?.id ?? 'random');
  }

  protected override onPointer(p: PointerInput): void {
    const i = BUTTONS.findIndex((b) => inRect(b.rect, p.x, p.y));
    if (i < 0) return;
    if (p.kind === 'move' && i !== this.sel) {
      this.sel = i;
      this.sound('move');
    } else if (p.kind === 'down' && p.button === 0) this.play(BUTTONS[i]?.id ?? 'random');
  }
}
